import type { DataSet } from "@incytestudios/ddv";
import type { MultiScenarioPumpChartContext } from "./d";
import { computed, ref } from "vue";
import type { OperatingParameters } from "@/composables/forms/formTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { dataSetToUserUnits } from "./getProducedTdhDataSets";

export const getRequiredTdhDataSets = (
  context: MultiScenarioPumpChartContext
): DataSet[] => {
  const {
    scenarios,
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    areScenariosEspShore
  } = context;

  const xLabel = `Flow Rate (${targetXUnitLabel})`;
  let yLabel: string;
  if (areScenariosEspShore) {
    yLabel = `Total Dynamic Head (${targetYUnitLabel})`;
  } else {
    yLabel = `Differential Pressure (${targetYUnitLabel})`;
  }
  return scenarios
    .map((s, i) => {
      const {
        scenario,
        correlations,
        wellProperties,
        equipment,
        parameters,
        pumps,
        seals,
        intakes,
        housings,
        cables,
        deviationSurvey,
        yToolBypassTubings,
        yToolEquipmentTubings
      } = s;

      const hypotheticalParameters = ref<OperatingParameters>({
        ...parameters,
        __typename: "scenario_parameters"
      });

      const {
        surfaceFlowRates,
        ascendingOrderPumps,
        maxFlowRateAtIntakeBBLD,
        pumpCalculations
      } = useScenarioCalculations({
        scenario,
        equipment,
        wellProperties,
        correlations,
        pumps,
        seals,
        intakes,
        housings,
        cables,
        deviationSurvey,
        yToolBypassTubings,
        yToolEquipmentTubings,
        parameters: hypotheticalParameters
      });

      const graphMax = computed(() =>
        Math.min(
          ...ascendingOrderPumps.value.map(
            xrefPump => pumpCalculations.individual.value[xrefPump.id].graphMax
          ),
          maxFlowRateAtIntakeBBLD.value
        )
      );

      const originX = surfaceFlowRates.value.liquidFlowRateSTBD;
      const THRESHOLD = 0.15;
      const high = originX + graphMax.value * THRESHOLD;
      const low = originX - graphMax.value * THRESHOLD;

      const hypotheticalFlowRates = computed(() =>
        Array.from({ length: 40 }, (_, i) => (graphMax.value / 40) * i)
      );

      const points = hypotheticalFlowRates.value.map(liquidFlowRateSTBD => {
        Object.assign(hypotheticalParameters.value || {}, {
          target_liquid_rate_std_barrel_day: liquidFlowRateSTBD
        });

        return {
          x: pumpCalculations.aggregate.dischargeFlowRateSTBD.value,
          y: areScenariosEspShore
            ? pumpCalculations.aggregate.requiredTotalDynamicHeadFt.value
            : pumpCalculations.aggregate.differentialPressurePSIA.value
        };
      });

      return {
        title: `${s.scenario?.title} - Required TDH`,
        xAxisLabel: xLabel,
        yAxisLabel: yLabel,
        color: `var(--plotColor${i})`,
        points: points
          .filter(pt => pt.x > low && pt.x < high)
          .filter(pt => pt.y > 0)
      };
    })
    .map(ds => {
      return dataSetToUserUnits(
        ds,
        baseXUnit,
        targetXUnit,
        baseYUnit,
        targetYUnit,
        xLabel,
        yLabel
      );
    });
};
